<script>
import { reactive } from 'vue'
import { mapMutations, mapActions } from 'vuex'
import * as def from '@/lib/definition'
import imageFileLoader from './image-file-loader'
import imageViewer from '@/components/image-viewer.vue'
import ffTextarea from '@/components/ff-textarea.vue'

export default {
  components: {
    imageFileLoader,
    imageViewer,
    ffTextarea
  },
  props: [
    'paragraphs',
    'mode'
  ],
  setup (props) {
    return reactive({
      def,
      currentIndex: null,
      showImageFileLoader: false,
      addImageIndex: null,
      focusPosition: 0
    })
  },
  computed: {
    ps () {
      const blank = {
        blank: true,
        type: 'text',
        content: ''
      }
      return [...this.paragraphs, blank].map(p => {
        p = { ...p }
        if (p.type === 'text') {
          const r = p.content.trim().replace(/\n/g, '<br>')
          const m = r.match(/https?:\/\/[a-zA-Z0-9-_]+\.[a-zA-Z0-9_/.?&#+-@]*/g)
          p.html = (() => {
            if (m && m.length) {
              return m.reduce((r, url) => {
                const a = `<a target="_blank" rel="noopener noreferrer" href="${url}">${url}</a>`
                return r.replace(url, a)
              }, r)
            } else {
              return r
            }
          })()
        }
        return p
      })
    }
  },
  methods: {
    ...mapMutations([
      'enqueueAsync',
      'dequeueAsync'
    ]),
    ...mapActions([
      'uploadStorage',
      'addImage'
    ]),
    uploadImageFile (file, thumb) {
      const { pageId } = this.$route.params
      const filename = Math.random().toString(32).substring(2)
      const fullPath = `pages/${pageId}/${filename}.${file.ext}`
      this.enqueueAsync()
      this.uploadStorage({
        fullPath,
        source: file.blob,
        metadata: {
          cacheControl: 'public,max-age=3600',
          customMetadata: { fileName: file.name }
        }
      })
        .then(() => {
          this.$emit('commit', {
            index: this.addImageIndex,
            replace: 0,
            value: [{
              type: 'image',
              content: fullPath
            }]
          })
          this.currentIndex = null
          this.addImageIndex = null
          this.showImageFileLoader = false
          this.addImage({ pageId, filename, fullPath })
          console.log('Uploaded a file!')
        })
        .catch(error => console.error(error))
        .finally(() => this.dequeueAsync())
      return false
    },
    stick ({ index, content }) {
      const before = this.ps[index - 1]
      if (index < 1 || before.type !== 'text') {
        return false
      }
      this.$emit('commit', {
        index: index - 1,
        value: [{
          ...before,
          content: `${before.content}\n${content}`
        }],
        replace: 2
      })
      this.currentIndex = this.currentIndex - 1
      this.focusPosition = before.content.length + 1
    },
    tab (e) {
      if (e.keyCode === 9) {
        e.preventDefault()
        if (e.shiftKey) {
          this.currentIndex = ((this.currentIndex - 1) + this.ps.length) % this.ps.length
        } else {
          this.currentIndex = (this.currentIndex + 1) % this.ps.length
        }
      }
    }
  }
}
</script>

<template>
  <div
    class="text-editor"
    :class="{ edit: mode === 'edit' }"
    @keydown="tab"
  >
    <div
      v-for="(p, index) in ps"
      :key="index"
      :class="{[p.type]: true}"
    >
      <template v-if="p.type=='text'">
        <div v-if="currentIndex === index && mode === 'edit'">
          <div class="control-panel level-left">
            <div class="change-text-weight">
              <button
                v-if="p.weight!=='heading'"
                @click="$emit('commit', { index, value: [{...p, weight: 'heading'}] })"
                class="button"
              >
                <i class="material-icons">title</i>
                <span>見出しにする</span>
              </button>
              <button
                v-else-if="p.weight==='heading'"
                @click="$emit('commit', { index, value: [{...p, weight: null}] })"
                class="button"
              >
                <i class="material-icons">title</i>
                <span>テキストにする</span>
              </button>
            </div>
            <div>
              <button
                @click="showImageFileLoader = true; addImageIndex = index"
                class="add-image button"
              >
                <i class="material-icons">add_circle</i>
                <span>{{ def.itemNames['images'] }}を追加する</span>
              </button>
            </div>
          </div>
          <ff-textarea
            :text="p.content"
            :key="'p' + index + paragraphs"
            :focusPosition="focusPosition"
            :weight="p.weight"
            :stick="ps[index - 1] && ps[index - 1].type === 'text'"
            @commit="$emit('commit', { index, value: $event })"
            @stick="stick({ index, content: $event })"
            :class="{heading: p.weight === 'heading'}"
          />
        </div>
        <p
          v-else-if="p.blank && mode === 'edit'"
          :key="'p' + index + p.content"
          @click="currentIndex = index; focusPosition = 0"
          class="prompt"
        >テキストを入力してください</p>
        <h2
          v-else-if="p.weight=='heading'"
          @click = "currentIndex = index; focusPosition = 0"
          class="is-size-3 has-text-weight-bold"
        >{{ p.content }}</h2>
        <p
          v-else
          v-html="p.html"
          :key="'p' + index + p.content"
          @click="currentIndex = index; focusPosition = 0"
        ></p>
      </template>
      <image-viewer
        v-else-if="p.type=='image'"
        :fullPath="p.content"
        tag="div"
        :key="'img' + index"
        @click = "currentIndex = index"
        class="level-left"
      >
        <div>
          <div v-if="mode==='edit'" class="image-target">
            <div class="level-left">
              <span class="is-size-5">URL</span>
              <input
                :value="p.target"
                @blur="$emit('commit', { index, value: [{...p, target: $event.target.value}] })"
                class="width-20rem"
                placeholder="画像にリンクを設定する時に入力します"
              >
            </div>
          </div>
          <div v-else-if="p.target" class="image-target">
            <p class="is-size-5">画像にリンクが設定されています。</p>
            <a
              :href="p.target"
              target="_blank"
              rel="noopener noreferrer"
              class="level-left button is-plain"
            >
              <span>{{ p.target }}</span>
              <i class="material-icons">open_in_new</i>
            </a>
          </div>
          <button
            v-if="mode==='edit'"
            class="remove-image icon"
            @click.stop="$emit('commit', { index, value: [] })"
          >
            <i class="material-icons">remove_circle</i>
          </button>
        </div>
      </image-viewer>
    </div>
    <transition name="fade">
      <image-file-loader
        v-if="showImageFileLoader"
        :maxSize="def.maxSize"
        @load="uploadImageFile($event)"
        @cancel="showImageFileLoader = false"
      />
    </transition>
  </div>
</template>

<style lang="scss" scoped>
@use "../styles/variables";
.text-editor {
  background-color: white;
  padding: 1rem;
  border-radius: .5rem;
  > * {
    margin: 2rem 0;
  }
  > .text {
    position: relative;
    p {
      padding: .25rem;
    }
    .control-panel {
      position: absolute;
      top: -2rem;
      left: 0;
    }
    .prompt {
      color: variables.$gray;
    }
  }
  h2 {
    padding: .25rem;
  }
  textarea {
    padding: .25rem;
    &.heading {
      font-weight: bold;
      font-size: 1.125rem;
    }
  }
  .change-text-weight,
  .add-image {
    background-color: variables.$gray-lighter;
  }
  .image-viewer{
    .remove-image {
      position: absolute;
      top: 0;
      margin-left: -1rem;
    }
    .image-target {
      margin: 1rem 0;
    }
  }
  &.edit {
    h2, p {
      border: solid 1px white;
      cursor: pointer;
      &:hover {
        border: solid 1px variables.$gray;
        border-radius: .5rem;
        box-sizing: border-box;
      }
    }
  }
}
</style>
<style lang="scss">
.text-editor {
  .image-viewer {
    > div {
      margin: 1rem;
    }
    img {
      max-width: 20rem;
      max-height: 20rem;
    }
  }
}
</style>
